/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect } from "react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { defaultTheme, darkTheme } from "@ag-festas/ui";

// Temas disponíveis. As cores de cada um vêm da própria paleta (sem hex copiado
// aqui). Os temas antigos "green" e "blue" saíram: nenhuma tela os oferecia, e
// quem os tinha salvo volta ao padrão (ver `lerTemaSalvo`).
const themes = {
  default: {
    name: "Atlantico (Padrao)",
    theme: defaultTheme,
    key: "default",
    description: "Tema editorial com tons de verde petroleo e cobre",
    primary: defaultTheme.palette.primary.main,
    secondary: defaultTheme.palette.secondary.main,
  },
  dark: {
    name: "Escuro",
    theme: darkTheme,
    key: "dark",
    description: "Tema escuro com contraste quente",
    primary: darkTheme.palette.primary.main,
    secondary: darkTheme.palette.secondary.main,
  },
};

// Preferência de tema (chaves do localStorage e leitura segura)
const CHAVE_TEMA = "efesta_theme";
const CHAVE_MODO = "efesta_dark_mode";
const CONSULTA_SISTEMA_ESCURO = "(prefers-color-scheme: dark)";

// localStorage pode lançar (modo privado, cookies bloqueados, cota cheia): a
// preferência então vale só nesta sessão, mas o app não cai.
const lerStorage = (chave) => {
  try {
    return window.localStorage.getItem(chave);
  } catch {
    return null;
  }
};
const gravarStorage = (chave, valor) => {
  try {
    window.localStorage.setItem(chave, valor);
  } catch {
    // sem persistência
  }
};
const removerStorage = (chave) => {
  try {
    window.localStorage.removeItem(chave);
  } catch {
    // sem persistência
  }
};

// Escolha explícita do usuário: true/false. `null` = nunca escolheu (segue o
// sistema). Qualquer outro valor salvo (corrompido) conta como "nunca escolheu".
const lerModoEscolhido = () => {
  const bruto = lerStorage(CHAVE_MODO);
  if (bruto === "true") return true;
  if (bruto === "false") return false;
  return null;
};

const lerTemaSalvo = () => {
  const chave = lerStorage(CHAVE_TEMA);
  // `themes[chave]` sozinho aceitaria "toString"/"constructor" (herdados do
  // protótipo) e derrubaria a montagem: só valem as chaves próprias.
  return chave && Object.prototype.hasOwnProperty.call(themes, chave)
    ? chave
    : "default";
};

const sistemaPrefereEscuro = () =>
  typeof window.matchMedia === "function" &&
  Boolean(window.matchMedia(CONSULTA_SISTEMA_ESCURO)?.matches);

// Context
const ThemeContext = createContext();

// Provider Component
export const ThemeProvider = ({ children }) => {
  // Tudo é lido de forma síncrona na criação do estado: o primeiro render já sai
  // no tema certo (um efeito só aplicaria a preferência depois do primeiro paint).
  const [currentTheme, setCurrentTheme] = useState(lerTemaSalvo);
  const [modoEscolhido, setModoEscolhido] = useState(lerModoEscolhido);
  const [sistemaEscuro, setSistemaEscuro] = useState(sistemaPrefereEscuro);

  // A escolha explícita vale mais; sem ela, vale o que o sistema prefere agora.
  const isDarkMode = modoEscolhido ?? sistemaEscuro;

  // Acompanha o sistema (ex.: o SO troca para escuro à noite). Só tem efeito
  // visível enquanto o usuário não escolheu.
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const consulta = window.matchMedia(CONSULTA_SISTEMA_ESCURO);
    if (!consulta) return undefined;

    const aoMudar = (evento) => setSistemaEscuro(Boolean(evento.matches));
    if (typeof consulta.addEventListener === "function") {
      consulta.addEventListener("change", aoMudar);
      return () => consulta.removeEventListener("change", aoMudar);
    }
    consulta.addListener?.(aoMudar);
    return () => consulta.removeListener?.(aoMudar);
  }, []);

  // Alterar tema (paleta)
  const changeTheme = (themeKey) => {
    if (themes[themeKey]) {
      setCurrentTheme(themeKey);
      gravarStorage(CHAVE_TEMA, themeKey);
    }
  };

  // Alternar claro/escuro: grava a escolha explícita (deixa de seguir o sistema).
  // O tema legado "dark" (paleta escura salva antes do alternador existir) conta
  // como escuro; ao voltar para o claro ele também sai da paleta escura.
  const toggleDarkMode = () => {
    const proximoEscuro = !(isDarkMode || currentTheme === "dark");
    setModoEscolhido(proximoEscuro);
    gravarStorage(CHAVE_MODO, JSON.stringify(proximoEscuro));
    if (!proximoEscuro && currentTheme === "dark") {
      setCurrentTheme("default");
      gravarStorage(CHAVE_TEMA, "default");
    }
  };

  // Resetar para o tema padrão e voltar a seguir o sistema
  const resetToDefault = () => {
    setCurrentTheme("default");
    setModoEscolhido(null);
    gravarStorage(CHAVE_TEMA, "default");
    removerStorage(CHAVE_MODO);
  };

  // Obter tema atual baseado nas configurações
  const getActiveTheme = () => {
    if (isDarkMode && currentTheme !== "dark") {
      return darkTheme;
    }
    return themes[currentTheme].theme;
  };

  // Obter informações do tema atual
  const getCurrentThemeInfo = () => {
    return themes[currentTheme];
  };

  // Obter lista de todos os temas
  const getAvailableThemes = () => {
    return Object.values(themes);
  };

  // Verificar se é tema escuro
  const isCurrentThemeDark = () => {
    return isDarkMode || currentTheme === "dark";
  };

  // Obter cor primária do tema atual
  const getPrimaryColor = () => {
    return getCurrentThemeInfo().primary;
  };

  // Obter cor secundária do tema atual
  const getSecondaryColor = () => {
    return getCurrentThemeInfo().secondary;
  };

  // O script do index.html pinta a raiz antes do primeiro paint; ao trocar o
  // tema depois, a raiz acompanha (senão o fundo antigo aparece no overscroll).
  const temaAtivo = getActiveTheme();
  useEffect(() => {
    const raiz = document.documentElement;
    raiz.style.backgroundColor = temaAtivo.palette.background.default;
    raiz.style.colorScheme = temaAtivo.palette.mode;
  }, [temaAtivo]);

  // Context value
  const contextValue = {
    // Estado
    currentTheme,
    isDarkMode,
    theme: getActiveTheme(),

    // Informações do tema
    themeInfo: getCurrentThemeInfo(),
    availableThemes: getAvailableThemes(),

    // Ações
    changeTheme,
    toggleDarkMode,
    resetToDefault,

    // Utilitários
    isCurrentThemeDark,
    getPrimaryColor,
    getSecondaryColor,

    // Constantes
    themes,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={getActiveTheme()}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

// Hook para usar o contexto
export const useTheme = () => {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error("useTheme deve ser usado dentro de um ThemeProvider");
  }

  return context;
};

// Hook para acessar apenas as cores do tema
export const useThemeColors = () => {
  const { theme, getPrimaryColor, getSecondaryColor } = useTheme();

  return {
    primary: getPrimaryColor(),
    secondary: getSecondaryColor(),
    palette: theme.palette,
  };
};

// Hook para verificar se é tema escuro
export const useIsDarkTheme = () => {
  const { isCurrentThemeDark } = useTheme();
  return isCurrentThemeDark();
};

export default ThemeContext;
